import React, { FC } from "react";
import { Handle, NodeProps, Position } from "reactflow";
import { useAppSelector } from "store/hooks";
import { NodeDevModeHighlighter } from "./NodeDevModeHighlighter";
import { CommonNodeData } from "./NodeData";

const compatNumberFormatter = Intl.NumberFormat("en", { notation: "compact" });

export interface AiResponseNodeData extends CommonNodeData {
  type: "aiResponse";
  channel?: "email" | "slack" | "sms";
  connectionId?: string;
  subject?: string;
  prompt?: string;
  agentId?: string;
  model?: string;
  maxTurns?: number;
  replyTimeoutMinutes?: number;
  waitForCustomerReplyBeforeFirstResponse?: boolean;
  replyOnly?: boolean;
  onComplete?: "advance" | "exit";
}

const channelLabel = (c?: string) => {
  switch (c) {
    case "email":
      return "Email";
    case "slack":
      return "Slack";
    case "sms":
      return "SMS";
    default:
      return "Set channel";
  }
};

export const AiResponseNode: FC<NodeProps<AiResponseNodeData>> = ({
  id,
  isConnectable,
  selected,
  data: { channel, prompt, showErrors, disabled, customersCount, maxTurns },
}) => {
  const { isViewMode } = useAppSelector((state) => state.flowBuilder);
  const promptPreview = (prompt || "").trim().slice(0, 80);

  return (
    <div
      className={`ai-response relative w-[260px] min-h-[100px] rounded bg-themed-card font-inter ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${
        selected ? "border-2 border-[#7C3AED]" : "border border-themed-border"
      }`}
    >
      <Handle
        position={Position.Top}
        type="target"
        isConnectable={isConnectable}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#7C3AED]">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Person head */}
            <circle
              cx="9"
              cy="7"
              r="2.5"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            {/* Person shoulders */}
            <path
              d="M3.75 18c0-2.9 2.35-5.25 5.25-5.25s5.25 2.35 5.25 5.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            {/* Microphone capsule */}
            <rect
              x="16.25"
              y="4.5"
              width="3.5"
              height="6"
              rx="1.75"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            {/* Mic stand arc */}
            <path
              d="M14.75 9.5c0 1.93 1.57 3.5 3.5 3.5s3.5-1.57 3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            {/* Mic stem + base */}
            <path
              d="M18.25 13v3M16.5 16h3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </Handle>
      <div className="p-[16px]">
        <div className="flex justify-between items-center font-semibold text-base mb-[2px]">
          <div>AI Response</div>
          {isViewMode && (
            <div className="h-fit px-1 py-[2px] flex items-center gap-[4px] bg-themed-background rounded-sm">
              <div className="text-[10px] leading-normal">
                {compatNumberFormatter.format(customersCount || 0)}
              </div>
            </div>
          )}
        </div>
        <div className="font-normal text-[12px] leading-[18px] text-themed-textSecondary mb-1">
          via {channelLabel(channel)} · max {maxTurns ?? 10} turns
        </div>
        <div
          className={`font-normal text-[12px] leading-[18px] ${
            showErrors && !promptPreview
              ? "text-[#F43F5E]"
              : "text-themed-textSecondary"
          }`}
        >
          {promptPreview || "Set a prompt"}
          {prompt && prompt.length > 80 ? "…" : ""}
        </div>
      </div>

      <Handle
        position={Position.Bottom}
        type="source"
        isConnectable={isConnectable}
        className="!min-h-[1px] !h-[1px] !bottom-[1px] !opacity-0 !border-0 pointer-events-none cursor-default"
      />
      <NodeDevModeHighlighter id={id} />
    </div>
  );
};
